import React, { useEffect, useMemo, useState } from 'react';
import {
  DRAWDOWN_NET_MAX,
  formatOverrideSummary,
  validateDrawdownYears,
} from '../incomeOverrides';

function compactSummary(overrides, validation) {
  if (!overrides.length) return 'Main target only';
  if (!validation.isValid) return 'Fix errors to apply';
  return validation.effectiveRows
    .map((row) => {
      const ages = row.fromAge === row.toAge ? `${row.fromAge}` : `${row.fromAge}–${row.toAge}`;
      return `${ages} £${Math.round(row.net / 1000)}k`;
    })
    .join(', ');
}

function RowInput({
  label, value, onCommit, min, max, className = '', error = false, prefix,
}) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(String(value));
    }
  }, [value, focused]);

  const commit = () => {
    const parsed = parseInt(String(draft).replace(/[^\d]/g, ''), 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    let next = parsed;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    onCommit(next);
  };

  return (
    <label className={`flex flex-col gap-1 min-w-0 ${className}`}>
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <div className="relative">
        {prefix && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[12px] pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            commit();
            setFocused(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(String(value));
              e.currentTarget.blur();
            }
          }}
          className={`drawdown-num-input rc-num-input w-full tabular-nums text-[13px]${prefix ? ' has-prefix' : ''}${error ? ' error' : ''}`}
        />
      </div>
    </label>
  );
}

export function customDrawdownYearsSummary(overrides, validation) {
  const resolved = validation ?? validateDrawdownYears(overrides, {
    retirementAge: overrides[0]?.fromAge ?? 0,
    lifeExpectancy: 90,
  });
  return compactSummary(overrides, resolved);
}

export default function IncomeOverridesEditor({
  overrides,
  onChange,
  lifeExpectancy,
  retirementAge,
  defaultNet,
  validation,
}) {
  const resolved = validation ?? validateDrawdownYears(overrides, { retirementAge, lifeExpectancy });

  const rowIssueMap = useMemo(() => {
    const map = new Map();
    resolved.rowIssues.forEach(({ index, issues }) => map.set(index, issues));
    return map;
  }, [resolved.rowIssues]);

  const updateRow = (index, patch) => {
    onChange(overrides.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index) => {
    onChange(overrides.filter((_, i) => i !== index));
  };

  const addRow = () => {
    const start = Math.min(retirementAge, lifeExpectancy);
    onChange([
      ...overrides,
      { fromAge: start, toAge: start, net: defaultNet, inflate: false },
    ]);
  };

  const rows = overrides.length > 0 ? overrides : [];

  return (
    <div>
      <style>{`
        .drawdown-num-input.rc-num-input {
          padding: 6px 8px;
          min-height: 34px;
        }
        .drawdown-num-input.rc-num-input.has-prefix {
          padding-left: 22px;
        }
      `}</style>
      <p className="text-[12px] text-slate-500 leading-relaxed mb-3" style={{ textWrap: 'pretty' }}>
        Override your main target for drawdown years only (from age {retirementAge} to plan age {lifeExpectancy}).
        Rows must not overlap.
      </p>

      {rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((row, index) => {
            const multiYear = row.toAge > row.fromAge;
            const issues = rowIssueMap.get(index) ?? [];
            const fromError = issues.some((m) => m.includes('From') || m.includes('after plan'));
            const toError = issues.some((m) => m.includes('To') || m.includes('Period falls'));
            const netError = issues.some((m) => m.includes('net') || m.includes('Net'));

            return (
              <div key={`drawdown-row-${index}`} className="relative pr-5">
                <div className="grid grid-cols-3 gap-2 items-end">
                  <RowInput
                    label="From"
                    value={row.fromAge}
                    min={retirementAge}
                    max={lifeExpectancy}
                    error={fromError}
                    onCommit={(fromAge) => updateRow(index, {
                      fromAge,
                      toAge: Math.max(fromAge, row.toAge),
                    })}
                  />
                  <RowInput
                    label="To"
                    value={row.toAge}
                    min={Math.max(retirementAge, row.fromAge)}
                    max={lifeExpectancy}
                    error={toError}
                    onCommit={(toAge) => updateRow(index, {
                      toAge,
                      fromAge: Math.min(row.fromAge, toAge),
                    })}
                  />
                  <RowInput
                    label="Net £/yr"
                    value={row.net}
                    min={1}
                    max={DRAWDOWN_NET_MAX}
                    prefix="£"
                    error={netError}
                    onCommit={(net) => updateRow(index, { net })}
                  />
                </div>
                {multiYear && (
                  <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rc-checkbox flex-shrink-0"
                      checked={row.inflate === true}
                      onChange={(e) => updateRow(index, { inflate: e.target.checked })}
                    />
                    <span className="text-[12px] text-slate-600 leading-snug">Increase by inflation</span>
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="absolute top-0 right-0 text-[14px] font-medium text-slate-400 hover:text-rose-600 leading-none"
                  aria-label="Remove row"
                >
                  ×
                </button>
                {issues.length > 0 && (
                  <p className="mt-1 text-[11.5px] text-rose-600">{issues.join(' ')}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[12px] font-medium text-emerald-700 hover:text-emerald-800"
      >
        + Add row
      </button>

      {resolved.globalError && (
        <p className="mt-2 text-[11.5px] text-rose-600">{resolved.globalError}</p>
      )}

      {resolved.isValid && resolved.effectiveRows.length > 0 && (
        <p className="mt-2 text-[11.5px] text-slate-500">
          {resolved.effectiveRows.map(formatOverrideSummary).join(' · ')}
        </p>
      )}
    </div>
  );
}
